import { z } from "zod";
import { DRYBULK_CARGO_OPS_LANG_BASE, INCREMENT_QUANTITY_PATTERN } from "../constants";

const L = `${DRYBULK_CARGO_OPS_LANG_BASE}.sequences.execution.increment`;

type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * DBCO-0048 (R-21) - the client-side help for the increment forms: ATS and ATC are not in the future, a corrected ATS is not later
 * than the stored one, the ATC is after the ATS, the quantity is positive with at most one decimal. The server is the authority
 * and every refusal it sends is rendered; these checks only save a round trip and never decide anything the server would not.
 */
export const startSchema = (t: Translate) =>
  z.object({
    ats: z
      .date()
      .nullable()
      .optional()
      .refine((value) => !value || value.getTime() <= Date.now(), t(`${L}.errors.ats-future`)),
  });

export const completeSchema = (t: Translate, storedAts: string | undefined) =>
  z
    .object({
      quantity: z
        .string()
        .trim()
        .min(1, t("form-validation.mandatory", { label: t(`${L}.fields.quantity`) }))
        .refine(
          (value) => value === "" || (INCREMENT_QUANTITY_PATTERN.test(value) && Number(value) > 0),
          t(`${L}.errors.quantity`),
        ),
      atc: z.date({
        required_error: t("form-validation.mandatory", { label: t(`${L}.fields.atc`) }),
        invalid_type_error: t("form-validation.mandatory", { label: t(`${L}.fields.atc`) }),
      }),
      ats: z.date().nullable().optional(),
      final: z.boolean(),
    })
    .superRefine((values, context) => {
      const stored = storedAts ? new Date(storedAts) : null;
      if (values.atc.getTime() > Date.now()) {
        context.addIssue({ code: "custom", path: ["atc"], message: t(`${L}.errors.atc-future`) });
      }
      if (values.ats && stored && values.ats.getTime() > stored.getTime()) {
        context.addIssue({
          code: "custom",
          path: ["ats"],
          message: t(`${L}.errors.ats-backwards`),
        });
      }
      const effectiveAts = values.ats ?? stored;
      if (effectiveAts && values.atc.getTime() <= effectiveAts.getTime()) {
        context.addIssue({
          code: "custom",
          path: ["atc"],
          message: t(`${L}.errors.atc-before-ats`),
        });
      }
    });
